import { useMemo, useState } from 'react'
import { cn } from '../../../shared/utils/cn'
import { SELF_TEST_OUTCOME_LABELS, SELF_TEST_OUTCOME_ORDER } from '../../../constants/selfTest'
import type { SelfTestOutcome, SelfTestReport, SelfTestResult } from '../../../types/selfTest'
import { SelfTestOutcomeBadge } from './SelfTestOutcomeBadge'

interface SelfTestResultsTableProps {
  report: SelfTestReport
  /** Re-run a single endpoint and fold the fresh row back into the report. */
  onRerun?: (path: string) => void
  rerunningPath?: string | null
}

type SortMode = 'outcome' | 'latency' | 'path'
type SortDirection = 'asc' | 'desc'

const OUTCOMES: SelfTestOutcome[] = ['pass', 'fail', 'not-covered']

interface SortHeaderProps {
  label: string
  mode: SortMode
  sortMode: SortMode
  direction: SortDirection
  onSort: () => void
  className?: string
}

/** aria-sort is only valid on the th, never on the button inside it. */
export function ariaSort(
  mode: SortMode,
  sortMode: SortMode,
  direction: SortDirection
): 'ascending' | 'descending' | 'none' {
  if (sortMode !== mode) return 'none'
  return direction === 'asc' ? 'ascending' : 'descending'
}

/**
 * A column header that looks clickable. The arrow is always rendered — greyed
 * when inactive — so the column advertises that it can be sorted before anyone
 * has to discover it by clicking.
 */
function SortHeader({ label, mode, sortMode, direction, onSort, className }: SortHeaderProps) {
  const active = sortMode === mode

  return (
    <button
      onClick={onSort}
      className={cn(
        'inline-flex items-center gap-1 uppercase hover:text-gray-900',
        active ? 'text-gray-900 font-semibold' : 'text-gray-500',
        className
      )}
    >
      {label}
      <span className={active ? 'text-gray-900' : 'text-gray-300'}>
        {active && direction === 'asc' ? '▲' : '▼'}
      </span>
    </button>
  )
}

function latencyClass(result: SelfTestResult): string {
  if (result.latency === 'very-slow') return 'text-red-600 font-medium'
  if (result.latency === 'slow') return 'text-amber-600'
  return 'text-gray-700'
}

export function SelfTestResultsTable({
  report,
  onRerun,
  rerunningPath,
}: SelfTestResultsTableProps) {
  // Show everything by default. The filters are there to narrow down to
  // failures when you want that; hiding passes up front makes the panel look
  // like it only found problems.
  const [visible, setVisible] = useState<Set<SelfTestOutcome>>(() => new Set(OUTCOMES))
  const [sortMode, setSortMode] = useState<SortMode>('outcome')
  const [direction, setDirection] = useState<SortDirection>('asc')
  const [expanded, setExpanded] = useState<string | null>(null)

  /** Re-clicking the active column flips direction; a new column starts fresh. */
  const sortBy = (mode: SortMode, initial: SortDirection) => {
    if (mode === sortMode) {
      setDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortMode(mode)
      setDirection(initial)
    }
  }

  /**
   * Bars scale against the slowest row actually shown, NOT latencyStats.max —
   * that stat covers passing probes only, so a 41s timeout beside a 1.2s max
   * pass produced a 3,294%-wide bar that ran off the page.
   */
  const barMax = useMemo(
    () => Math.max(1, ...report.results.map((r) => r.latencyMs ?? 0)),
    [report.results]
  )

  const rows = useMemo(() => {
    const filtered = report.results.filter((r) => visible.has(r.outcome))
    const flip = direction === 'asc' ? -1 : 1

    return [...filtered].sort((a, b) => {
      if (sortMode === 'latency') {
        // Untimed rows (not-covered) sink to the bottom in BOTH directions —
        // ascending by latency should surface the fastest endpoint, not the
        // ones that were never called.
        if (a.latencyMs === undefined && b.latencyMs === undefined) {
          return a.path.localeCompare(b.path)
        }
        if (a.latencyMs === undefined) return 1
        if (b.latencyMs === undefined) return -1
        return (b.latencyMs - a.latencyMs) * flip
      }

      if (sortMode === 'path') {
        return a.path.localeCompare(b.path) * (direction === 'asc' ? 1 : -1)
      }

      const byOutcome =
        (SELF_TEST_OUTCOME_ORDER[a.outcome] - SELF_TEST_OUTCOME_ORDER[b.outcome]) *
        (direction === 'asc' ? 1 : -1)
      return byOutcome !== 0 ? byOutcome : a.path.localeCompare(b.path)
    })
  }, [report.results, visible, sortMode, direction])

  const toggle = (outcome: SelfTestOutcome) => {
    setVisible((current) => {
      const next = new Set(current)
      if (next.has(outcome)) next.delete(outcome)
      else next.add(outcome)
      return next
    })
  }

  const countFor = (outcome: SelfTestOutcome) =>
    outcome === 'pass'
      ? report.totals.pass
      : outcome === 'fail'
        ? report.totals.fail
        : report.totals.notCovered

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Endpoints</h2>
        <div className="flex flex-wrap gap-2">
          {OUTCOMES.map((outcome) => (
            <button
              key={outcome}
              onClick={() => toggle(outcome)}
              aria-pressed={visible.has(outcome)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium',
                visible.has(outcome)
                  ? 'border-gray-400 bg-gray-100 text-gray-800'
                  : 'border-gray-200 text-gray-400 hover:text-gray-600'
              )}
            >
              {SELF_TEST_OUTCOME_LABELS[outcome]} ({countFor(outcome)})
            </button>
          ))}
        </div>
      </div>

      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
          <tr>
            <th className="py-2 pr-4" aria-sort={ariaSort('outcome', sortMode, direction)}>
              <SortHeader
                label="Outcome"
                mode="outcome"
                sortMode={sortMode}
                direction={direction}
                onSort={() => sortBy('outcome', 'asc')}
              />
            </th>
            <th className="py-2 pr-4" aria-sort={ariaSort('path', sortMode, direction)}>
              <SortHeader
                label="Path"
                mode="path"
                sortMode={sortMode}
                direction={direction}
                onSort={() => sortBy('path', 'asc')}
              />
            </th>
            <th className="py-2 pr-4 text-right">Status</th>
            <th className="py-2 pr-4" aria-sort={ariaSort('latency', sortMode, direction)}>
              <SortHeader
                label="Latency"
                mode="latency"
                sortMode={sortMode}
                direction={direction}
                onSort={() => sortBy('latency', 'desc')}
                className="w-full justify-end"
              />
            </th>
            <th className="py-2 pr-4">Reason</th>
            {onRerun && <th className="py-2 pr-4 text-right">Re-run</th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={onRerun ? 6 : 5} className="py-3 text-center text-gray-500">
                No endpoints match the current filter.
              </td>
            </tr>
          )}

          {rows.map((result) => {
            const key = `${result.method} ${result.path}`
            const isExpanded = expanded === key
            const canExpand = Boolean(result.bodySnippet || result.url || result.hint)

            return [
              <tr
                key={key}
                onClick={() => canExpand && setExpanded(isExpanded ? null : key)}
                className={cn(
                  'border-b border-gray-100 last:border-0',
                  canExpand && 'cursor-pointer hover:bg-gray-50'
                )}
              >
                <td className="py-2 pr-4">
                  <SelfTestOutcomeBadge outcome={result.outcome} />
                </td>
                <td className="py-2 pr-4 font-mono text-gray-700">{result.path}</td>
                <td className="py-2 pr-4 text-right tabular-nums text-gray-700">
                  {result.status ?? '—'}
                </td>
                <td className={cn('py-2 pr-4 text-right tabular-nums', latencyClass(result))}>
                  {result.latencyMs !== undefined ? `${result.latencyMs.toLocaleString()} ms` : '—'}
                  {/* Outcome and latency are independent: a correct-but-slow
                      endpoint passes. Naming the band stops a green badge beside
                      a red number from reading as a contradiction. */}
                  {(result.latency === 'slow' || result.latency === 'very-slow') && (
                    <span className="block text-xs font-normal">
                      {result.latency === 'very-slow' ? 'very slow' : 'slow'}
                    </span>
                  )}
                  {result.latencyMs !== undefined && (
                    <span className="mt-1 block h-1 w-full overflow-hidden rounded bg-gray-100">
                      <span
                        className={cn(
                          'block h-1 rounded',
                          result.latency === 'very-slow'
                            ? 'bg-red-400'
                            : result.latency === 'slow'
                              ? 'bg-amber-400'
                              : 'bg-blue-400'
                        )}
                        style={{
                          // Clamped as well as scaled: overflow-hidden on the
                          // track is the belt, this is the braces.
                          width: `${Math.min(100, Math.max(2, (result.latencyMs / barMax) * 100))}%`,
                        }}
                      />
                    </span>
                  )}
                </td>
                <td className="py-2 pr-4 text-gray-600">{result.reason ?? ''}</td>
                {onRerun && (
                  <td className="py-2 pr-4 text-right">
                    {result.outcome !== 'not-covered' && (
                      <button
                        onClick={(event) => {
                          // The row itself toggles the detail panel.
                          event.stopPropagation()
                          onRerun(result.path)
                        }}
                        disabled={Boolean(rerunningPath)}
                        className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {rerunningPath === result.path ? '…' : 'Run'}
                      </button>
                    )}
                  </td>
                )}
              </tr>,

              isExpanded && (
                <tr key={`${key}-detail`} className="border-b border-gray-100">
                  <td colSpan={onRerun ? 6 : 5} className="py-2 pr-4">
                    {result.url && (
                      <p className="text-xs text-gray-500">
                        Requested <span className="font-mono">GET {result.url}</span>
                      </p>
                    )}
                    {result.hint && <p className="text-xs text-gray-500">{result.hint}</p>}
                    {result.bodySnippet && (
                      <pre className="mt-2 max-h-48 overflow-auto rounded bg-gray-50 p-3 text-xs text-gray-700">
                        {result.bodySnippet}
                      </pre>
                    )}
                  </td>
                </tr>
              ),
            ]
          })}
        </tbody>
      </table>
    </section>
  )
}
